import React, { useEffect, useState } from 'react'
import { ArrowRight, Eye } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import LanguageTranslator from '../../../../locals/LanguageTranslator'
import { formatNumber, getRelativeDueString } from '../../../../helper/genericFunction'
import { CardIcon } from './BusinessDashboardCard'
import { EARLY_PAYMENT_SUMMARY } from '../businessDashboardConstants'
import { dispatchHandler } from '../../../../helper/apiHelperFunctions'

const EarlyPaymentRequestCard = () => {
    const dispatch = useDispatch();
    const getEarlyPaymentInvoicesData = useSelector(state => state?.digivendStore?.getEarlyPaymentInvoicesData || {});
    const getPaymentRequestSummaryData = useSelector(state => state?.digivendStore?.getPaymentRequestSummaryData || {});

    const [invoices, setInvoices] = useState([]);
    const [summaryTiles, setSummaryTiles] = useState(
        (EARLY_PAYMENT_SUMMARY || []).map(item => ({
            ...item,
            value: item.id === "discount" ? "0%" : (item.id === "eligible" ? 0 : "₹ 0"),
        }))
    );

    useEffect(() => {
        const payload = {
            type: 1,
            pageNo: 1,
            pageSize: 10,
            sortedBy: "",
            sortedIn: "",
            search: "",
            filter: {},
            status: "ELIGIBLE_INVOICES",
            displayName: "VENDOR_PENDING_INVOICES"
        };
        dispatchHandler(dispatch, "getEarlyPaymentInvoicesRequest", payload);

        dispatchHandler(dispatch, "getPaymentRequestSummaryRequest", {
            type: 1,
            pageNo: 1,
            pageSize: 10,
            sortedBy: "",
            sortedIn: "",
            search: "",
            filter: {},
            status: "REQUESTED",
            displayName: "VEN_INVOICE_PAYMENT_REQUEST_PENDING"
        });
    }, [dispatch]);

    useEffect(() => {
        if (getPaymentRequestSummaryData?.isSuccess) {
            const resource = getPaymentRequestSummaryData?.data?.data?.resource || getPaymentRequestSummaryData?.data?.resource || {};

            setSummaryTiles(prev => prev.map(item => {
                if (item.id === "requested") return { ...item, value: `₹ ${formatNumber(resource?.requestedAmount || 0).short}` };
                if (item.id === "processed") return { ...item, value: `₹ ${formatNumber(resource?.processedAmount || 0).short}` };
                if (item.id === "discount") return { ...item, value: `${resource?.['avg.DiscountOffered'] || 0}%` };
                return item;
            }));
        }
    }, [getPaymentRequestSummaryData?.isSuccess, getPaymentRequestSummaryData?.data]);

    useEffect(() => {
        if (getEarlyPaymentInvoicesData?.isSuccess) {
            const container = getEarlyPaymentInvoicesData?.data?.data || getEarlyPaymentInvoicesData?.data || {};
            const returnedData = container?.resource || [];
            const list = Array.isArray(returnedData) ? returnedData : (returnedData?.list || returnedData?.data || []);
            const totalCount = container?.totalRecord ?? container?.totalCount ?? container?.totalRecords ?? list.length;

            setInvoices(list);

            setSummaryTiles(prev => prev.map(item => item.id === "eligible"
                ? { ...item, value: totalCount }
                : item
            ));
        }
    }, [getEarlyPaymentInvoicesData?.isSuccess, getEarlyPaymentInvoicesData?.data]);

    return (
        <div className={`bg-white rounded-[14px] border border-[#F1F5F9] shadow-[0px_1px_1.5px_0px_rgba(0,0,0,0.1),0px_1px_1px_0px_rgba(0,0,0,0.1)] p-[21px] flex flex-col gap-[20px] ${(getEarlyPaymentInvoicesData?.isLoading || getPaymentRequestSummaryData?.isLoading) ? 'opacity-50 pointer-events-none' : ''}`}>
            <LanguageTranslator tag='p' className='mb-0 text-[13px] font-semibold text-fontBlack'>Early Payment Request</LanguageTranslator>

            <div className='grid grid-cols-2 gap-[12px]'>
                {summaryTiles?.map(item => item?.type === "icon" ? (
                    <div key={item?.id} className='bg-white border border-borderSlateGrey rounded-[10px] p-[11px] flex flex-col gap-[10px]'>
                        <div className='flex items-center justify-between gap-2'>
                            <LanguageTranslator tag='p' className='mb-0 capitalize text-[11px] font-normal leading-[1.4] text-[#4A5D7A]'>{item?.label}</LanguageTranslator>
                            <CardIcon icon={item?.icon} background={item?.iconBg} />
                        </div>
                        <p className='mb-0 text-[16px] font-semibold text-[#111D31] whitespace-nowrap'>{item?.value}</p>
                    </div>
                ) : (
                    <div key={item?.id} className='rounded-[10px] p-[11px] flex flex-col gap-[6px]' style={{ backgroundColor: item?.background }}>
                        <LanguageTranslator tag='p' className='mb-0 capitalize text-[11px] font-normal leading-[1.4] text-[#4A5D7A]'>{item?.label}</LanguageTranslator>
                        <p className='mb-0 text-[22px] font-semibold whitespace-nowrap' style={{ color: item?.valueColor }}>{item?.value}</p>
                    </div>
                ))}
            </div>

            <div className='flex flex-col gap-[8px] overflow-y-auto max-h-[300px] pr-[4px]'>
                {invoices?.map((item, idx) => (
                    <div key={item?.vendor_document_no || item?.invoice || item?.id || idx} className='bg-[#F8FAFC] border border-[#F1F5F9] rounded-[10px] px-[13px] py-[11px] flex items-center justify-between gap-3'>
                        <div className='flex-1 min-w-0 flex flex-col items-start'>
                            <p className='mb-0 text-[12px] font-semibold text-[#111D31]'>{item?.vendor_document_no || item?.invoice || item?.invoiceNumber}</p>
                            <LanguageTranslator tag='p' className='mb-0 text-[11px] font-normal text-[#4A5D7A]'>{item?.due_date ? getRelativeDueString(item.due_date) : item?.due || `Due in ${item?.dueDays || 30} days`}</LanguageTranslator>
                        </div>
                        <div className='shrink-0 flex flex-col items-end pr-[12px]'>
                            <p className='mb-0 text-[12px] font-semibold text-[#111D31] whitespace-nowrap'>
                                {(item?.pending_amount || item?.net_amount) ? `₹${formatNumber(item.pending_amount || item.net_amount).short}` : item?.amount || (item?.invoiceAmount ? `₹${formatNumber(item?.invoiceAmount).short}` : item?.amountLarge)}
                            </p>
                            <LanguageTranslator tag='p' className='mb-0 text-[11px] font-normal text-[#4A5D7A] whitespace-nowrap'>Invoice Amount</LanguageTranslator>
                        </div>
                        <button type='button' className='shrink-0 bg-[#5D5FEF] h-[26px] w-[100px] rounded-[3px] flex items-center justify-center gap-[6px]'>
                            <LanguageTranslator tag='span' className='text-[12px] font-medium text-white'>Request</LanguageTranslator>
                            <ArrowRight size={14} color='#FFFFFF' strokeWidth={1.6} />
                        </button>
                    </div>
                ))}
            </div>

            <button type='button' className='bg-[#EAEBF7] h-[32px] w-full rounded-[3px] flex items-center justify-center gap-[6px]'>
                <Eye size={16} color='#111D31' strokeWidth={1.5} />
                <LanguageTranslator tag='span' className='text-[12px] font-medium text-[#111D31]'>View all eligible invoices</LanguageTranslator>
            </button>
        </div>
    )
}

export default EarlyPaymentRequestCard
